/**
 * The colour tokens of tokens.css as values, for Ant Design's theme (which
 * derives hover/active shades from real colours, not CSS variables).
 * palette.test.ts checks that both files agree.
 */
export type ColorScheme = 'light' | 'dark';

export interface Palette {
  canvas: string;
  surface: string;
  'surface-2': string;
  hover: string;
  text: string;
  'text-2': string;
  'text-3': string;
  edge: string;
  boundary: string;
  'dis-bg': string;
  'dis-fg': string;
  surround: string;
  acc: string;
  'acc-hover': string;
  'on-acc': string;
  'acc-soft': string;
  'acc-ink': string;
  danger: string;
  'danger-soft': string;
  success: string;
  'success-soft': string;
  warning: string;
  'warning-soft': string;
  'tip-bg': string;
  'tip-fg': string;
  'shell-fill': string;
  'shell-line': string;
}

export const palettes: Record<ColorScheme, Palette> = {
  light: {
    canvas: '#f1f5f8',
    surface: '#ffffff',
    'surface-2': '#eaf0f4',
    hover: '#e1e9ef',
    text: '#13202b',
    'text-2': '#4e6373',
    'text-3': '#647482',
    edge: '#d2dde5',
    boundary: '#718593',
    'dis-bg': '#e3eaef',
    'dis-fg': '#7a8a96',
    surround: '#dae3ea',
    acc: '#0068a0',
    'acc-hover': '#005484',
    'on-acc': '#ffffff',
    'acc-soft': '#e2f1f8',
    'acc-ink': '#005484',
    danger: '#b42318',
    'danger-soft': '#fbeae8',
    success: '#1b7a4b',
    'success-soft': '#e3f3ea',
    warning: '#9a5b00',
    'warning-soft': '#fbf0dc',
    'tip-bg': '#13202b',
    'tip-fg': '#f1f5f8',
    'shell-fill': '#ffffff',
    'shell-line': '#d3dee6',
  },
  dark: {
    canvas: '#0e141a',
    surface: '#172029',
    'surface-2': '#202b35',
    hover: '#27333e',
    text: '#f1f5f8',
    'text-2': '#b3c1cc',
    'text-3': '#8d9ca8',
    edge: '#32404c',
    boundary: '#7d8d9a',
    'dis-bg': '#202b35',
    'dis-fg': '#7a8a96',
    surround: '#080c10',
    acc: '#5dc0e8',
    'acc-hover': '#7fceee',
    'on-acc': '#04263a',
    'acc-soft': '#123246',
    'acc-ink': '#8fd5f2',
    danger: '#ff8a7a',
    'danger-soft': '#3a2220',
    success: '#7bd3a4',
    'success-soft': '#173426',
    warning: '#f2b45a',
    'warning-soft': '#3a2d17',
    'tip-bg': '#e6edf2',
    'tip-fg': '#13202b',
    'shell-fill': '#1d2730',
    'shell-line': '#34424e',
  },
};

/** Viewport widths where the layout changes (spec §2), in CSS px. */
export const BREAKPOINTS = {
  /** Below: reduced spacing, recent Q&As modal, icon-only attach, no size column. */
  compact: 640,
  /** Below: logo mark only, Sign out moves into Preferences. */
  narrow: 760,
  /** Below: prompt editing and CSV move into More; selected items below the list. */
  medium: 900,
  /** Below: the Guidelines & Context tab label shortens to Context. */
  tabs: 1000,
  /** Below: the 280 px folder tree becomes a drawer. */
  tree: 1024,
  /** Below: the source panel is a modal; otherwise it docks right. */
  dock: 1100,
} as const;
